import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AuthClient from "../../api/AuthClient";
import BookingType from "../../api/enums/BookingType";
import GetHostChatMessages from "../../api/GetHostChatMessagesApi";
import { usePromoterSelectedGroup } from "../../hooks/usePromoterSelectedGroup";
import { routes } from "../../routes/routes";
import { HostChatMessage, PartyModel } from "../../types";
import { DateFormat, formatDate } from "../../utils/datePareser";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import Header from "../Header";
import PromoterGroupActions from "../PromoterGroups/PromoterGroupActions";
import PromoterGroupGenderCounts from "../PromoterGroups/PromoterGroupGenderCounts";
import PromoterGroupPhotoStrip from "../PromoterGroups/PromoterGroupPhotoStrip";
import PromoterRequestedClubsList from "../PromoterGroups/PromoterRequestedClubsList";
import PromoterSentOffersList from "../PromoterGroups/PromoterSentOffersList";
import "../PromotersPages/promotersPages.scss";

const getBookingTypeLabel = (bookingType: BookingType | string): string => {
  const bookingTypeName =
    typeof bookingType === "string" ? bookingType : BookingType[bookingType];

  switch (bookingTypeName) {
    case "GuestList":
      return "Guestlist";
    case "TableBooking":
      return "Table";
    default:
      return bookingTypeName ?? "";
  }
};

const PromoterGroupDetails = () => {
  const { city, groupId } = useParams();
  const { selectedGroup, requestState } = usePromoterSelectedGroup(groupId);
  const navigate = useNavigate();

  const invitations = useMemo(
    () => selectedGroup?.invitations ?? [],
    [selectedGroup]
  );
  const returnTo = routes.PromGroupDetailPage(city ?? "", groupId ?? "");

  const acceptedPromoterOffer = useMemo(
    () =>
      selectedGroup?.promotersOffers.find((promoterOffer) =>
        promoterOffer.offers.some((offer) => offer.isAccepted === true)
      ),
    [selectedGroup]
  );
  const acceptedOffer = useMemo(
    () =>
      acceptedPromoterOffer?.offers.find((offer) => offer.isAccepted === true),
    [acceptedPromoterOffer]
  );

  const openChats = (partyGroup: PartyModel) => {
    const groupAdminInvitation =
      partyGroup.invitations.find((invitation) => invitation.isGroupAdmin) ??
      partyGroup.invitations[0];

    if (!acceptedPromoterOffer || !acceptedOffer?.id) {
      return;
    }

    const offerWithPartyDate = {
      ...acceptedOffer,
      partyDate: partyGroup.party.partyDate,
    };
    const hostChatMessages = new GetHostChatMessages(new AuthClient());

    hostChatMessages.getChatMessages(acceptedOffer.id).then((response) => {
      const messages: HostChatMessage[] | undefined = response.data;

      navigate(routes.ChatPages, {
        state: {
          messages,
          offer: offerWithPartyDate,
          groupAdminName: `${groupAdminInvitation?.firstName ?? ""} ${groupAdminInvitation?.lastName ?? ""}`.trim(),
          groupAdminId: groupAdminInvitation?.id,
          promoterName: acceptedPromoterOffer.promoter.FirstName,
          promoterId: acceptedPromoterOffer.promoter.id,
          currentUserId: acceptedPromoterOffer.promoter.id,
          returnTo,
        },
      });
    });
  };

  return (
    <>
      <Header />
      <div className="centerGroup">
        <div className="desktopUI">
          <div className="row userProfile_Header">
            <Breadcrumbs to={routes.PromotersMode} />
            <PromoterGroupActions
              city={city}
              groupId={groupId}
              groupStatus={selectedGroup?.party.status}
              returnTo={returnTo}
            />
          </div>

          {selectedGroup ? (
            <div className="promoter-group-detail-page">
              <div className="promoter-group-detail-container">
                <div className="promoter-group-detail-date">
                  {formatDate(
                    new Date(selectedGroup.party.partyDate),
                    false,
                    DateFormat.Short
                  )}
                </div>

                <div className="promoter-group-detail-summary">
                  <span className="promoter-group-detail-summary__label">
                    {getBookingTypeLabel(selectedGroup.party.bookingType)}
                  </span>
                  <span className="promoter-group-detail-summary__city">
                    {selectedGroup.party.city}
                  </span>
                </div>

                <section className="promoter-group-detail-section">
                  <h3 className="promoter-group-detail-section__title">GUESTS</h3>
                  <PromoterGroupPhotoStrip
                    invitations={invitations}
                    city={city}
                    groupId={groupId}
                    callbackUrl="promoter-group-detail"
                    className="promoter-group-detail-photo-strip mt-3"
                    trackClassName="promoter-group-detail-photo-strip__track"
                    imageClassName="promoter-group-detail-photo-strip__image"
                  />
                </section>

                <div className="promoter-group-detail-counts">
                  <PromoterGroupGenderCounts invitations={invitations} />
                </div>

                <PromoterSentOffersList
                  city={selectedGroup.party.city}
                  groupId={selectedGroup.party.id}
                  promotersOffers={selectedGroup.promotersOffers}
                />

                <PromoterRequestedClubsList
                  city={selectedGroup.party.city}
                  requestedListings={selectedGroup.requestedListings}
                />

                {acceptedOffer ? (
                  <button
                    className="promoter-group-detail__chat-button"
                    type="button"
                    onClick={() => openChats(selectedGroup)}
                  >
                    Chat with organiser
                  </button>
                ) : null}
              </div>
            </div>
          ) : requestState === "loading" || requestState === "idle" ? (
            <p className="text-white text-center mt-4">Loading group...</p>
          ) : (
            <p className="text-white text-center mt-4">Group not found.</p>
          )}
        </div>
      </div>
      <ToastContainer
        progressClassName="toastProgress"
        bodyClassName="toastBody"
      />
    </>
  );
};

export default PromoterGroupDetails;
